import { FC, Key, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Card, Divider, Flex, Tree } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  ProfileModal,
  DeleteModal,
  AuthorizedButton,
  PageTitle,
} from '../../../components';
import { ProfileCard } from './components';
import styles from './index.module.scss';
import useHttpService from '../../../services/hook';
import {
  IProfileSettingsInitialValues,
  IPermissionTree,
  IRole,
  IRolePermissions,
} from '../../../types';
import { userActions } from '../../../store/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '../../../store';
import validationSchema from './validations';
import { modalTypes } from '../../../assets/enums';
import AuthService from '../../../api-clients/AuthService';
import { getFormikValuesFromRole } from '../../../utils/functions';

const ProfileSettings: FC = () => {
  const organizations = useAppSelector(
    (state) => state.organizationReducer.organizations,
  );

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<
    IRolePermissions | undefined
  >(undefined);
  const [roles, setRoles] = useState<IRole[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [permissionTree, setPermissionTree] = useState<
    IPermissionTree | undefined
  >(undefined);

  const executeService = useHttpService();
  const dispatch = useAppDispatch();
  const { role, organization } = useAppSelector(
    (state) => state.userReducer.userInfo,
  );
  const authService = useMemo(
    () => new AuthService(executeService),
    [executeService],
  );

  const INITIAL_VALUES: IProfileSettingsInitialValues = {
    name: '',
    leftPermissions: [],
    rightPermissions: [],
    organization,
  };

  // Handlers
  const handleOpenModal = () => {
    setSelectedItem(undefined);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleClickEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
  };

  const handleClickDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const onLeftCheck = (
    checkedKeysValue:
      | {
          checked: Key[];
          halfChecked: Key[];
        }
      | Key[],
  ) => {
    formik.setFieldValue('leftPermissions', checkedKeysValue);
  };

  const onRightCheck = (
    checkedKeysValue:
      | {
          checked: Key[];
          halfChecked: Key[];
        }
      | Key[],
  ) => {
    formik.setFieldValue('rightPermissions', checkedKeysValue);
  };

  const handleFetchRoles = async () => {
    try {
      const response = await authService.fetchRoles();
      if (response && Array.isArray(response)) {
        setRoles(response);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleFetchPermission = async () => {
    const response = await authService.fetchPermissionTree();
    if (response && 'left' in response) {
      setPermissionTree(response);
    }
  };

  const handleChangeRole = async (id: string) => {
    const selectedItem = await authService.fetchRolePermissions(id);
    if (selectedItem && 'id' in selectedItem) {
      setSelectedItem(selectedItem);
    }
  };

  const handleFetchUserPermissions = async () => {
    const response = await authService.fetchRolePermissions(role.id);
    if (response && 'id' in response) {
      dispatch(userActions.changeUserPermissions(response.permissions));
    }
  };

  const onSubmit = async (values: IProfileSettingsInitialValues) => {
    try {
      if (selectedItem) {
        await authService.assignPermissions(selectedItem.id, values);
      } else {
        await authService.createRole(values);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsModalOpen(false);
      setIsEditModalOpen(false);
      handleFetchRoles();
      handleFetchUserPermissions();
    }
  };

  const onCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const onConfirm = async () => {
    try {
      if (selectedItem?.id) {
        await authService.deleteRole(selectedItem.id);
      }
    } catch (e) {
      console.log(e);
    } finally {
      handleFetchRoles();
      setIsDeleteModalOpen(false);
      setSelectedItem(undefined);
    }
  };

  const formik = useFormik<IProfileSettingsInitialValues>({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit,
  });

  useEffect(() => {
    handleFetchRoles();
    handleFetchPermission();
  }, []);

  useEffect(() => {
    if (selectedItem && permissionTree) {
      const values = getFormikValuesFromRole(selectedItem, permissionTree);
      formik.setValues(values);
    } else {
      formik.setValues(INITIAL_VALUES);
    }
  }, [selectedItem]);

  return (
    <Flex vertical gap={10} className={styles.container}>
      <PageTitle title="Kullanıcı Profilleri & Yetkilendirme" />
      <Flex justify="flex-end">
        <AuthorizedButton
          model="extendedgroup"
          action="add"
          className={styles.addButton}
          onClick={handleOpenModal}
        >
          <PlusOutlined className={styles.buttonIcon} />
          <div className={styles.buttonText}>Yeni Profil Oluştur</div>
        </AuthorizedButton>
      </Flex>
      <Card>
        <Flex className={styles.bottomContainer} justify="space-between">
          <ProfileCard
            selectedItem={selectedItem}
            roles={roles}
            handleClickEdit={handleClickEdit}
            handleClickDelete={handleClickDelete}
            handleChangeRole={handleChangeRole}
          />
          <Divider type="vertical" className={styles.divider} />
          <Flex justify="space-between" className={styles.treeContainer}>
            <Tree
              checkable
              defaultExpandAll
              disabled={true}
              treeData={permissionTree?.left}
              className={styles.customCheckbox}
              onCheck={onLeftCheck}
              checkedKeys={formik.values.leftPermissions}
            />
            <Tree
              checkable
              defaultExpandAll
              disabled={true}
              treeData={permissionTree?.right}
              className={styles.customCheckbox}
              onCheck={onRightCheck}
              checkedKeys={formik.values.rightPermissions}
            />
          </Flex>
        </Flex>
      </Card>
      <ProfileModal
        type={modalTypes.CREATE}
        open={isModalOpen}
        title="Yeni Profil Oluşturma"
        subTitle="Yeni Kullanıcı Profili Oluşturma"
        formik={formik}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseModal}
        organizations={organizations}
        permissionTree={permissionTree}
      />
      <ProfileModal
        type={modalTypes.EDIT}
        open={isEditModalOpen}
        title="Profil Güncelleme"
        subTitle="Kullanıcı Profil Güncelleme"
        formik={formik}
        handleSubmit={formik.handleSubmit}
        handleCancel={handleCloseEdit}
        organizations={organizations}
        permissionTree={permissionTree}
      />
      <DeleteModal
        role={selectedItem?.external_name}
        count={selectedItem?.connected_users}
        default={selectedItem?.default}
        open={isDeleteModalOpen}
        handleOk={onConfirm}
        handleCancel={onCancel}
      />
    </Flex>
  );
};

export default ProfileSettings;
